// src/components/CinematicBackground.tsx
import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface CinematicBackgroundProps {
  children: React.ReactNode;
}

const CinematicBackground: React.FC<CinematicBackgroundProps> = ({ children }) => {
  const noiseCanvasRef = useRef<HTMLCanvasElement>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    // Generate noise texture once on mount - finer particles (10,000 points)
    const canvas = noiseCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Increase canvas resolution for finer grain
    const width = 800;
    const height = 800;
    canvas.width = width;
    canvas.height = height;

    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;

    // Generate 10,000 fine noise particles (matching Android exactly)
    for (let i = 0; i < 10000; i++) {
      const x = Math.floor(Math.random() * width);
      const y = Math.floor(Math.random() * height);
      const index = (y * width + x) * 4;

      // White pixels with 8% opacity (matching Android)
      data[index] = 255;     // R
      data[index + 1] = 255; // G
      data[index + 2] = 255; // B
      data[index + 3] = 20;  // A (8% opacity ≈ 20/255)
    }

    ctx.putImageData(imageData, 0, 0);
  }, []);

  return (
    <div
      className="relative min-h-screen w-full overflow-x-clip transition-colors duration-500"
      style={{ background: 'var(--nc-bg-gradient)' }}
    >
      {/* Base Background Layer - Theme Gradient */}
      <div
        className="fixed inset-0 transition-all duration-500 pointer-events-none"
        style={{ background: 'var(--nc-bg-gradient)' }}
      />

      {/* ── Ambient Glow Layer ──
           All glows live in ONE inset-0 div → no box edges, no clipping, no sharp lines.
           Multiple radial-gradients are stacked in a single `background` value. */}
      <div
        className="fixed inset-0 pointer-events-none transition-all duration-700"
        style={{
          background: isDark
            ? `
                radial-gradient(ellipse 140% 130% at 0% 0%,     rgba(168, 85, 247, 0.32) 0%, rgba(168, 85, 247, 0) 60%),
                radial-gradient(ellipse 140% 130% at 100% 100%, rgba(99, 102, 241, 0.32)  0%, rgba(99,  102, 241, 0) 60%)
              `
            : `
                radial-gradient(ellipse 150% 140% at 0%   0%,   rgba(251, 191,  36, 0.32) 0%, rgba(251, 191,  36, 0) 60%),
                radial-gradient(ellipse 150% 140% at 100% 100%, rgba( 99, 102, 241, 0.30) 0%, rgba( 99, 102, 241, 0) 60%),
                radial-gradient(ellipse 120% 110% at 100%   0%, rgba(244, 114, 182, 0.18) 0%, rgba(244, 114, 182, 0) 60%),
                radial-gradient(ellipse 120% 100% at 50%  110%, rgba(251, 191,  36, 0.10) 0%, rgba(251, 191,  36, 0) 60%)
              `,
        }}
      />

      {/* Grid Pattern Layer with Fade-Out Effect */}
      <div
        className="fixed inset-0 pointer-events-none transition-all duration-500"
        style={{
          backgroundImage: `
            linear-gradient(to right, var(--color-grid-line, rgba(255, 255, 255, 0.12)) 1px, transparent 1px),
            linear-gradient(to bottom, var(--color-grid-line, rgba(255, 255, 255, 0.12)) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          maskImage: isDark
            ? 'radial-gradient(ellipse at center, rgba(0,0,0,0.5) 20%, transparent 70%)'
            : 'radial-gradient(ellipse at center, rgba(0,0,0,0.6) 30%, transparent 80%)',
          WebkitMaskImage: isDark
            ? 'radial-gradient(ellipse at center, rgba(0,0,0,0.5) 20%, transparent 70%)'
            : 'radial-gradient(ellipse at center, rgba(0,0,0,0.6) 30%, transparent 80%)',
        }}
      />

      {/* Noise/Grain Overlay - Fine Particles */}
      <canvas
        ref={noiseCanvasRef}
        className="fixed inset-0 w-full h-full pointer-events-none transition-opacity duration-500"
        style={{
          mixBlendMode: isDark ? 'screen' : 'multiply',
          opacity: isDark ? 0.7 : 0.12,
          imageRendering: 'auto',
          objectFit: 'cover',
        }}
      />

      {/* Content Layer */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default CinematicBackground;
